import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Check, FileText, Gift, Smartphone, Zap } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-3 pb-12 md:pt-8 md:pb-24 overflow-hidden bg-clinical-base" data-id="element-148">
      <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none" data-id="element-149"></div>
      <div className="absolute top-10 -left-32 w-96 h-96 bg-clinical-softer rounded-full blur-[100px] opacity-50 pointer-events-none" data-id="element-150"></div>
      <div className="absolute bottom-0 right-0 w-[32rem] h-[32rem] bg-clinical-soft rounded-full blur-[140px] opacity-40 pointer-events-none" data-id="element-151"></div>

      <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-152">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-6 lg:gap-10 items-center" data-id="element-153">
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left" data-id="element-154">
            {/* Top Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-clinical-line bg-white mb-4" data-id="element-155">
              <div className="flex items-center gap-1" data-id="element-156">
                <Star size={12} className="fill-clinical-accent text-clinical-accent" data-id="element-157" />
                <span className="text-xs font-semibold text-clinical-deep" data-id="element-158">
                  4.9/5
                </span>
              </div>
              <span className="text-clinical-line" data-id="element-159">·</span>
              <span className="text-xs font-medium text-clinical-ink" data-id="element-160">
                +300 estudiantes
              </span>
            </motion.div>

            {/* Eyebrow */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-mid mb-4" data-id="element-161">
              Atlas Clínico · Mega Pack Enfermería
            </motion.div>

            {/* Emotional Headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="font-heading font-black leading-[1.05] mb-4 md:mb-5 text-[1.55rem] sm:text-[2.2rem] md:text-[2.6rem] lg:text-[3rem] text-clinical-deep tracking-tight" data-id="element-162">
              El momento más{' '}
              <span className="accent-italic font-bold" data-id="element-163">desesperante</span> de
              enfermería no es el parcial. <br className="hidden md:block" data-id="element-164" />
              Es cuando el paciente te mira…{' '}
              <span className="accent-italic font-bold" data-id="element-165">
                y no sabés qué hacer.
              </span>
            </motion.h1>

            {/* Subheadline / Promise */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-base md:text-xl text-clinical-ink mb-6 max-w-xl leading-relaxed font-medium" data-id="element-166">
              El sistema clínico de 6 tomos para{' '}
              <span className="font-bold text-clinical-deep" data-id="element-167">
                actuar con seguridad
              </span>{' '}
              frente al paciente y saber exactamente qué hacer.
            </motion.p>

            {/* Mobile Mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.35,
            duration: 0.6
          }} className="lg:hidden relative w-full max-w-[360px] mb-6" data-id="element-168">
              <div className="absolute inset-0 bg-clinical-softer blur-[60px] rounded-full scale-90 pointer-events-none" data-id="element-169"></div>
              <img src="https://cdn.magicpatterns.com/uploads/j6sRYcK5X4hdn124VLpYBV/Copia_de_Diseno_sin_titulo.png" alt="Mega Pack Enfermería" width={720} height={540} loading="eager"
            // @ts-ignore — valid HTML attribute, React supports it
            fetchpriority="high" decoding="async" className="relative w-full h-auto drop-shadow-[0_24px_48px_rgba(11,42,74,0.28)]" data-id="element-170" />
            </motion.div>

            {/* CTA — dominant with Price */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="w-full flex flex-col items-center lg:items-start" data-id="element-171">
              <a href="#pricing" className="group relative inline-flex items-center justify-center gap-2.5 bg-clinical-cta hover:bg-clinical-ctaHover text-white font-bold text-base sm:text-lg px-7 sm:px-10 py-5 rounded-full transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_16px_40px_rgba(15,98,254,0.45)] hover:shadow-[0_20px_50px_rgba(15,98,254,0.6)] w-full max-w-[440px] sm:w-auto mb-4 uppercase tracking-[0.04em] text-center touch-manipulation" data-id="element-172">
                <span className="absolute inset-0 rounded-full bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-173"></span>
                <span className="relative" data-id="element-174">Quiero acceder ahora</span>
                <ArrowRight size={20} className="relative group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-175" />
              </a>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-2.5 gap-y-1 text-[11px] sm:text-xs text-clinical-ink/80 font-medium" data-id="element-176">
                <div className="flex items-center gap-1.5" data-id="element-177">
                  <Zap size={12} className="text-clinical-mid" data-id="element-178" />
                  <span data-id="element-179">Acceso inmediato</span>
                </div>
                <span className="text-clinical-line" data-id="element-180">·</span>
                <div className="flex items-center gap-1.5" data-id="element-181">
                  <Smartphone size={12} className="text-clinical-mid" data-id="element-182" />
                  <span data-id="element-183">Celular, tablet y PC</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Desktop Mockup — bigger */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4,
          duration: 0.8
        }} className="hidden lg:flex relative justify-center lg:justify-end w-full" data-id="element-184">
            <div className="relative w-full max-w-[680px]" data-id="element-185">
              <div className="absolute inset-0 bg-clinical-softer blur-[100px] rounded-full scale-90 pointer-events-none" data-id="element-186"></div>

              <motion.img src="https://cdn.magicpatterns.com/uploads/j6sRYcK5X4hdn124VLpYBV/Copia_de_Diseno_sin_titulo.png" alt="Mega Pack Enfermería" width={720} height={540} loading="eager"
            // @ts-ignore — valid HTML attribute, React supports it
            fetchpriority="high" decoding="async" className="relative w-full h-auto drop-shadow-[0_30px_60px_rgba(11,42,74,0.3)] animate-float-slow" data-id="element-187" />

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.8
            }} className="absolute top-10 -right-3 bg-white px-4 py-3 rounded-2xl shadow-[0_12px_32px_rgba(11,42,74,0.15)] border border-clinical-line flex items-center gap-3" data-id="element-188">
                <div className="w-10 h-10 rounded-xl bg-clinical-soft flex items-center justify-center" data-id="element-189">
                  <FileText className="w-5 h-5 text-clinical-mid" data-id="element-190" />
                </div>
                <div data-id="element-191">
                  <p className="text-sm font-bold text-clinical-deep" data-id="element-192">
                    6 Tomos PDF
                  </p>
                  <p className="text-[11px] text-clinical-ink/70" data-id="element-193">
                    Descarga inmediata
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1
            }} className="absolute -bottom-3 right-10 bg-white px-4 py-3 rounded-2xl shadow-[0_12px_32px_rgba(11,42,74,0.15)] border border-clinical-line flex items-center gap-3" data-id="element-194">
                <div className="w-10 h-10 rounded-xl bg-clinical-soft flex items-center justify-center" data-id="element-195">
                  <Gift className="w-5 h-5 text-clinical-mid" data-id="element-196" />
                </div>
                <div data-id="element-197">
                  <p className="text-sm font-bold text-clinical-deep" data-id="element-198">
                    6 Bonos gratis
                  </p>
                  <p className="text-[11px] text-clinical-ink/70" data-id="element-199">
                    Valor $53.000
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}